import { useEffect, useRef, useState } from "react";

import { Echo } from "@/lib/echo";
import { useFetchMe } from "@/user/services";
import { useTenant } from "@/user/services/tenant";

import { type WhisperUser } from "../types";

export function useConversationChannel(conversationId: number | undefined) {
  const { data: user } = useFetchMe();
  const tenant = useTenant();
  const [typingUsers, setTypingUsers] = useState<WhisperUser[]>([]);

  const timeoutPerUser = useRef<Record<number, NodeJS.Timeout>>({});

  useEffect(() => {
    if (!conversationId) return;

    Echo.private(
      `${tenant.id}.messenger.chat.${conversationId}`,
    ).listenForWhisper("typing", (response: WhisperUser) => {
      setTypingUsers((prev) => {
        if (prev.find((user) => user.id === response.id)) {
          return prev;
        }

        return [...prev, response];
      });

      if (timeoutPerUser.current[response.id]) {
        clearTimeout(timeoutPerUser.current[response.id]);
        delete timeoutPerUser.current[response.id];
      }

      timeoutPerUser.current[response.id] = setTimeout(() => {
        setTypingUsers((prev) =>
          prev.filter((user) => user.id !== response.id),
        );
      }, 3000);
    });

    return () => {
      Echo.leave(`messenger.chat.${conversationId}`);
    };
  }, [tenant.id, conversationId]);

  return {
    typingUsers,
    onTyping: () => {
      if (!conversationId || !user) return;

      Echo.private(`${tenant.id}.messenger.chat.${conversationId}`).whisper(
        "typing",
        {
          id: user!.id,
          first_name: user!.first_name,
          last_name: user!.last_name,
          profile_picture_thumb: user!.profile_picture_thumb,
        } satisfies WhisperUser,
      );
    },
  };
}
